import { useEffect, useState } from "react";
import "@/App.css";
import api from "@/lib/api";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import FeaturesPage from "@/pages/FeaturesPage";
import IDCApp from "@/pages/IDCApp";
import AdminPanel from "@/pages/AdminPanel";
import LockedScreen from "@/pages/LockedScreen";
import PublicDataEntry from "@/pages/PublicDataEntry";
import PasswordLink from "@/pages/PasswordLink";
import EventsApp from "@/pages/EventsApp";
import PublicEventForm from "@/pages/PublicEventForm";
import PublicMasterForm from "@/pages/PublicMasterForm";
import PublicScanner from "@/pages/PublicScanner";
import PublicPass from "@/pages/PublicPass";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

function getShareToken() {
  const m = window.location.hash.match(/^#\/share\/([A-Za-z0-9-]+)/);
  return m ? m[1] : null;
}

function getPwToken() {
  const m = window.location.hash.match(/^#\/pw\/([A-Za-z0-9-]+)/);
  return m ? m[1] : null;
}

function getEventToken() {
  const m = window.location.hash.match(/^#\/event\/([A-Za-z0-9-]+)/);
  return m ? m[1] : null;
}

function getMasterToken() {
  const m = window.location.hash.match(/^#\/master\/([A-Za-z0-9-]+)/);
  return m ? m[1] : null;
}

function getScanToken() {
  const m = window.location.hash.match(/^#\/scan\/([A-Za-z0-9-]+)/);
  return m ? m[1] : null;
}

function getPassToken() {
  const m = window.location.hash.match(/^#\/pass\/([A-Za-z0-9-]+)/);
  return m ? m[1] : null;
}

function checkIsAdmin() {
  const p = window.location.pathname.toLowerCase();
  const h = window.location.hash.toLowerCase();
  const s = window.location.search.toLowerCase();
  return p.startsWith("/admin") || h.includes("admin") || s.includes("admin");
}

function App() {
  const [shareToken, setShareToken] = useState(getShareToken);
  const [pwToken, setPwToken] = useState(getPwToken);
  const [eventToken, setEventToken] = useState(getEventToken);
  const [masterToken, setMasterToken] = useState(getMasterToken);
  const [scanToken, setScanToken] = useState(getScanToken);
  const [passToken, setPassToken] = useState(getPassToken);
  const [isAdminRoute, setIsAdminRoute] = useState(checkIsAdmin);
  const [shop, setShop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [activeFeature, setActiveFeature] = useState(null);

  useEffect(() => {
    const handleUrlChange = () => {
      setIsAdminRoute(checkIsAdmin());
      setShareToken(getShareToken());
      setPwToken(getPwToken());
      setEventToken(getEventToken());
      setMasterToken(getMasterToken());
      setScanToken(getScanToken());
      setPassToken(getPassToken());
    };
    window.addEventListener("popstate", handleUrlChange);
    window.addEventListener("hashchange", handleUrlChange);
    return () => {
      window.removeEventListener("popstate", handleUrlChange);
      window.removeEventListener("hashchange", handleUrlChange);
    };
  }, []);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("ps_token");
    if (!token) {
      setLoading(false);
      return;
    }
    api
      .get("/auth/me")
      .then((r) => setShop(r.data))
      .catch(() => localStorage.removeItem("ps_token"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!shop?.valid_until) return;
    if (sessionStorage.getItem("ps_plan_notified")) return;
    const end = new Date(shop.valid_until).getTime();
    const now = Date.now();
    if (end <= now) return;
    const days = Math.ceil((end - now) / 86400000);
    const till = new Date(end).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
    const label = shop.plan_active ? "plan" : "free trial";
    const msg = days <= 3
      ? `Your ${label} expires in ${days} day${days === 1 ? "" : "s"} (till ${till}). Contact admin to activate.`
      : `Your ${label} is active — ${days} days left (till ${till})`;
    toast[days <= 3 ? "warning" : "info"](msg, { duration: 6000 });
    sessionStorage.setItem("ps_plan_notified", "1");
  }, [shop]);

  const handleInstall = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    await installPrompt.userChoice;
    setInstallPrompt(null);
  };

  const handleLogout = () => {
    localStorage.removeItem("ps_token");
    sessionStorage.removeItem("ps_plan_notified");
    setShop(null);
    setActiveFeature(null);
  };

  const handleAdminExit = () => {
    if (window.location.hash.includes("admin")) {
      window.location.hash = "";
    }
    if (window.location.pathname.startsWith("/admin")) {
      window.history.pushState(null, "", "/");
    }
    setIsAdminRoute(false);
  };

  if (passToken) {
    return (
      <div className="App">
        <PublicPass token={passToken} />
        <Toaster position="top-center" richColors />
      </div>
    );
  }

  if (eventToken) {
    return (
      <div className="App">
        <PublicEventForm token={eventToken} />
        <Toaster position="top-center" richColors />
      </div>
    );
  }

  if (masterToken) {
    return (
      <div className="App">
        <PublicMasterForm token={masterToken} />
        <Toaster position="top-center" richColors />
      </div>
    );
  }

  if (scanToken) {
    return (
      <div className="App">
        <PublicScanner token={scanToken} />
        <Toaster position="top-center" richColors />
      </div>
    );
  }

  if (pwToken) {
    return (
      <div className="App">
        <PasswordLink token={pwToken} />
        <Toaster position="top-center" richColors />
      </div>
    );
  }

  if (shareToken) {
    return (
      <div className="App">
        <PublicDataEntry token={shareToken} />
        <Toaster position="top-center" richColors />
      </div>
    );
  }

  if (isAdminRoute) {
    return (
      <div className="App">
        <AdminPanel onExit={handleAdminExit} />
        <Toaster position="top-center" richColors />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background" data-testid="app-loading">
        <img src="/logo.webp?v=2" alt="Sootrik" className="h-14 animate-pulse" />
      </div>
    );
  }

  const locked = shop && shop.valid_until && new Date(shop.valid_until).getTime() < Date.now();

  if (locked) {
    return (
      <div className="App">
        <LockedScreen shop={shop} onLogout={handleLogout} />
        <Toaster position="top-center" richColors />
      </div>
    );
  }

  return (
    <div className="App">
      {shop ? (
        activeFeature === "orders" ? (
          <Dashboard
            shop={shop}
            onLogout={handleLogout}
            onInstall={handleInstall}
            canInstall={!!installPrompt}
            onBack={() => setActiveFeature(null)}
            onShopUpdate={setShop}
          />
        ) : activeFeature === "id-certificate" ? (
          <IDCApp
            shop={shop}
            onLogout={handleLogout}
            onInstall={handleInstall}
            onBack={() => setActiveFeature(null)}
            onShopUpdate={setShop}
          />
        ) : activeFeature === "events" ? (
          <EventsApp
            shop={shop}
            onLogout={handleLogout}
            onInstall={handleInstall}
            onBack={() => setActiveFeature(null)}
            onShopUpdate={setShop}
          />
        ) : (
          <FeaturesPage
            shop={shop}
            onLogout={handleLogout}
            onInstall={handleInstall}
            canInstall={!!installPrompt}
            onSelect={setActiveFeature}
            onShopUpdate={setShop}
          />
        )
      ) : (
        <Login onLogin={setShop} />
      )}
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;
